import { useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import PanelHeader from '@App/components/PanelHeader/PanelHeader';
import PanelList from '@App/components/PanelList/PanelList';
import {
  useMunicipalityDetails,
  useStatisticalSectorDetails,
} from '@services/query.service';
import { formatEnergyPerYear } from '@utils/energy.utils';
import { getFeaturesBounds, getGeometryAreaKm2 } from '@utils/geometry.utils';

import { layers } from './statistical-sector.layers';

import './statistical-sector.css';

const numberFormatter = new Intl.NumberFormat('nl-BE', {
  maximumFractionDigits: 2,
});

function formatNumber(value) {
  return value === undefined || value === null
    ? '-'
    : numberFormatter.format(value);
}

function formatArea(value) {
  return value === undefined || value === null
    ? '-'
    : `${numberFormatter.format(value)} km²`;
}

function formatPercentage(value) {
  return value === undefined || value === null
    ? '-'
    : `${numberFormatter.format(value * 100)}%`;
}

function StatisticalSector() {
  const { map } = useOutletContext();
  const { municipalityId, statisticalSectorId } = useParams();
  const { data: municipality } = useMunicipalityDetails(municipalityId);
  const { data: statisticalSector } = useStatisticalSectorDetails(statisticalSectorId);
  const { Naam: municipalityName } = municipality || {};
  const { Naam: sectorName } = statisticalSector || {};
  const areaKm2 = getGeometryAreaKm2(statisticalSector?.geometry);
  const statisticalSectorDetails = [
    ['Inwonersaantal', formatNumber(statisticalSector?.Inwonersaantal)],
    ['Oppervlakte', formatArea(areaKm2)],
    ['Warmtevraag', formatEnergyPerYear(statisticalSector?.warmtevraag)],
    [
      'Warmte ontvangen',
      formatEnergyPerYear(
        statisticalSector?.warmte_ontvangen_combosleutel_bs6_vs1,
      ),
    ],
    [
      'AQ-potentieel',
      formatNumber(statisticalSector?.AQ_score_combosleutel_bs6_vs1),
    ],
    [
      'Percentage ontvangen',
      formatPercentage(statisticalSector?.pct_ontvangen_combosleutel_bs6_vs1),
    ],
    [
      'Warmtevraag score',
      formatNumber(statisticalSector?.warmtevraag_score),
    ],
  ];

  useEffect(() => {
    if (!map) return undefined;

    const previousMinZoom = map.getMinZoom();
    map.setMinZoom(10);

    return () => {
      map.setMinZoom(previousMinZoom);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !municipalityId || !statisticalSectorId || !statisticalSector) {
      return undefined;
    }

    let hasFittedBounds = false;

    const syncStatisticalSector = () => {
      if (!map.getSource('gemeente') || !map.getSource('stat-sector')) return;

      if (!map.getLayer(layers.municipalityMask.id)) {
        map.addLayer(layers.municipalityMask);
      }

      map.setFilter(layers.municipalityMask.id, [
        '!=',
        ['get', 'ID_gemeente'],
        municipalityId,
      ]);

      if (!map.getLayer(layers.selectedStatisticalSector.id)) {
        map.addLayer(layers.selectedStatisticalSector);
      }

      const sectorFilter = ['==', ['get', 'ID_stat_sect'], statisticalSectorId];
      map.setFilter(layers.selectedStatisticalSector.id, sectorFilter);

      const sectorBounds = getFeaturesBounds([statisticalSector]);

      if (sectorBounds && !hasFittedBounds) {
        hasFittedBounds = true;
        map.fitBounds(sectorBounds, {
          padding: 48,
          maxZoom: 16,
          duration: 1000,
        });
        map.off('idle', syncStatisticalSector);
      }
    };

    syncStatisticalSector();
    map.on('idle', syncStatisticalSector);

    return () => {
      map.off('idle', syncStatisticalSector);
      if (map.getLayer(layers.municipalityMask.id)) {
        map.removeLayer(layers.municipalityMask.id);
      }
      if (map.getLayer(layers.selectedStatisticalSector.id)) {
        map.removeLayer(layers.selectedStatisticalSector.id);
      }
    };
  }, [map, municipalityId, statisticalSector, statisticalSectorId]);

  return (
    <div className="statistical-sector">
      <PanelHeader
        map={map}
        municipalityId={municipalityId}
        municipalityName={municipalityName}
        sectorName={sectorName}
      />
      <PanelList details={statisticalSectorDetails} />
    </div>
  );
}

export default StatisticalSector;
